import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, Bell, Search, Zap, Users, Bot, HelpCircle, Settings, Grid3x3, ChevronLeft, ChevronRight, } from 'lucide-react';
import { useAppTheme } from '../../hooks/useAppTheme';
import { haptics } from '../../utils/haptics';
import { MobileSettingsBottomSheet } from './MobileSettingsBottomSheet';
const NAV_ITEMS = [
    {
        id: 'my-office',
        label: 'My Office',
        icon: Home,
        path: '/app/my-office',
    },
    {
        id: 'inbox',
        label: 'Inbox',
        icon: Bell,
        path: '/app/inbox',
    },
    {
        id: 'missions',
        label: 'Missions',
        icon: Zap,
        path: '/app/missions',
    },
    {
        id: 'insights',
        label: 'Insights',
        icon: Search,
        path: '/app/insights',
    },
    {
        id: 'teams',
        label: 'Teams',
        icon: Users,
        path: '/app/teams',
    },
    {
        id: 'agents',
        label: 'Agents',
        icon: Bot,
        path: '/app/agents',
    },
    {
        id: 'help',
        label: 'Help',
        icon: HelpCircle,
        path: '/app/help',
    },
    {
        id: 'app-switcher',
        label: 'Apps',
        icon: Grid3x3,
        path: '/app/app-switcher',
    },
    {
        id: 'settings',
        label: 'Settings',
        icon: Settings,
        path: '/app/settings',
    },
];
export function MobileBottomNav({ onNavigation }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { isDark } = useAppTheme();
    const scrollContainerRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [activeItem, setActiveItem] = useState('my-office');
    const [showSettings, setShowSettings] = useState(false);
    // Initialize component
    useEffect(() => {
        return () => { };
    }, []);
    // Determine active item from current location
    useEffect(() => {
        const currentPath = location.pathname;
        const currentItem = NAV_ITEMS.find((item) => currentPath.includes(item.id));
        if (currentItem) {
            setActiveItem(currentItem.id);
        }
    }, [location.pathname]);
    // Check scroll position to show/hide scroll indicators
    const checkScrollPosition = () => {
        if (!scrollContainerRef.current)
            return;
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    };
    useEffect(() => {
        checkScrollPosition();
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', checkScrollPosition);
            window.addEventListener('resize', checkScrollPosition);
        }
        return () => {
            if (container) {
                container.removeEventListener('scroll', checkScrollPosition);
                window.removeEventListener('resize', checkScrollPosition);
            }
        };
    }, []);
    // Smooth scroll function
    const scroll = (direction) => {
        if (!scrollContainerRef.current)
            return;
        const scrollAmount = 120;
        const newScrollLeft = scrollContainerRef.current.scrollLeft +
            (direction === 'left' ? -scrollAmount : scrollAmount);
        scrollContainerRef.current.scrollTo({
            left: newScrollLeft,
            behavior: 'smooth',
        });
        haptics.light();
    };
    const handleNavigation = (item) => {
        setActiveItem(item.id);
        haptics.light();
        // Open settings bottom sheet instead of navigating
        if (item.id === 'settings') {
            setShowSettings(true);
        }
        else {
            navigate(item.path);
        }
        onNavigation?.(item.id);
    };
    // Colors and styling
    const bgColor = isDark ? 'rgba(15, 29, 46, 0.8)' : 'rgba(245, 243, 240, 0.85)';
    const borderColor = isDark ? 'rgba(14, 165, 233, 0.2)' : 'rgba(0, 102, 255, 0.1)';
    const shadowColor = isDark
        ? '0 8px 32px rgba(0, 0, 0, 0.5), 0 0 1px rgba(14, 165, 233, 0.2)'
        : '0 8px 32px rgba(0, 102, 255, 0.1), 0 0 1px rgba(0, 102, 255, 0.1)';
    const activeColor = isDark ? '#0ea5e9' : '#0066FF';
    const inactiveColor = isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)';
    return (_jsxs("div", { className: "fixed bottom-0 left-0 right-0 z-[9999] pointer-events-auto", style: {
            paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
        }, children: [_jsx("div", { className: "flex items-center justify-center px-3 pointer-events-auto", children: _jsxs("div", { className: "relative w-full max-w-2xl rounded-2xl overflow-hidden pointer-events-auto", style: {
                        backgroundColor: bgColor,
                        backdropFilter: 'blur(40px)',
                        WebkitBackdropFilter: 'blur(40px)',
                        border: `1px solid ${borderColor}`,
                        boxShadow: shadowColor,
                    }, children: [_jsx(AnimatePresence, { children: canScrollLeft && (_jsx(motion.button, { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, onClick: () => scroll('left'), onTap: () => scroll('left'), className: "absolute left-0 top-0 bottom-0 z-20 w-12 flex items-center justify-center backdrop-blur-sm pointer-events-auto hover:opacity-100 transition-opacity cursor-pointer", style: {
                                    background: isDark
                                        ? 'linear-gradient(to right, rgba(15, 29, 46, 0.95), rgba(15, 29, 46, 0.7), transparent)'
                                        : 'linear-gradient(to right, rgba(245, 243, 240, 0.95), rgba(245, 243, 240, 0.7), transparent)',
                                }, title: "Scroll left", type: "button", children: _jsx(ChevronLeft, { className: "h-6 w-6 flex-shrink-0", style: { color: inactiveColor } }) })) }), _jsxs("div", { ref: scrollContainerRef, className: "flex items-center gap-2 px-2 py-3 overflow-x-auto overflow-y-hidden pointer-events-auto", style: {
                                WebkitOverflowScrolling: 'touch',
                                scrollBehavior: 'smooth',
                                msOverflowStyle: 'none',
                                scrollbarWidth: 'none',
                                paddingLeft: canScrollLeft ? '48px' : '16px',
                                paddingRight: canScrollRight ? '48px' : '16px',
                                transition: 'padding 0.2s ease',
                            }, children: [_jsx("style", { children: `
              div::-webkit-scrollbar {
                display: none;
              }
              div::-webkit-scrollbar-track {
                display: none;
              }
              div::-webkit-scrollbar-thumb {
                display: none;
              }
            ` }), NAV_ITEMS.map((item) => {
                                    const Icon = item.icon;
                                    const isActive = activeItem === item.id;
                                    const handleClick = () => {
                                        handleNavigation(item);
                                    };
                                    return (_jsxs("button", { onClick: handleClick, onMouseUp: handleClick, onTouchEnd: handleClick, className: "relative flex-shrink-0 flex flex-col items-center justify-center p-2 rounded-lg transition-colors group pointer-events-auto hover:opacity-90 active:scale-95", title: item.label, type: "button", style: {
                                            minWidth: '68px',
                                            minHeight: '68px',
                                            touchAction: 'manipulation',
                                            pointerEvents: 'auto',
                                            cursor: 'pointer',
                                            WebkitUserSelect: 'none',
                                            userSelect: 'none',
                                            backgroundColor: isActive ? (isDark ? 'rgba(14, 165, 233, 0.1)' : 'rgba(0, 102, 255, 0.08)') : 'transparent',
                                        }, children: [isActive && (_jsx(motion.div, { layoutId: "navIndicator", className: "absolute inset-0 rounded-lg pointer-events-none", style: {
                                                    backgroundColor: isDark
                                                        ? 'rgba(14, 165, 233, 0.1)'
                                                        : 'rgba(0, 102, 255, 0.08)',
                                                }, transition: {
                                                    type: 'spring',
                                                    stiffness: 500,
                                                    damping: 30,
                                                } })), _jsx(motion.div, { animate: {
                                                    scale: isActive ? 1.1 : 1,
                                                    color: isActive ? activeColor : inactiveColor,
                                                }, transition: { type: 'spring', stiffness: 400, damping: 30 }, children: _jsx(Icon, { className: "h-6 w-6", strokeWidth: 2 }) }), _jsx(motion.span, { initial: { opacity: 0, fontSize: '10px' }, animate: {
                                                    opacity: isActive ? 1 : 0,
                                                    fontSize: isActive ? '11px' : '10px',
                                                }, transition: { duration: 0.15 }, className: "text-xs font-medium mt-1 text-center leading-none max-w-[60px]", style: { color: isActive ? activeColor : inactiveColor }, children: item.label.split(' ')[0] }), isActive && (_jsx(motion.div, { layoutId: "activeDot", className: "absolute bottom-0.5 h-1 w-1 rounded-full", style: { backgroundColor: activeColor }, transition: {
                                                    type: 'spring',
                                                    stiffness: 500,
                                                    damping: 30,
                                                } }))] }, item.id));
                                })] }), _jsx(AnimatePresence, { children: canScrollRight && (_jsx(motion.button, { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, onClick: () => scroll('right'), onTap: () => scroll('right'), className: "absolute right-0 top-0 bottom-0 z-20 w-12 flex items-center justify-center backdrop-blur-sm pointer-events-auto hover:opacity-100 transition-opacity cursor-pointer", style: {
                                    background: isDark
                                        ? 'linear-gradient(to left, rgba(15, 29, 46, 0.95), rgba(15, 29, 46, 0.7), transparent)'
                                        : 'linear-gradient(to left, rgba(245, 243, 240, 0.95), rgba(245, 243, 240, 0.7), transparent)',
                                }, title: "Scroll right", type: "button", children: _jsx(ChevronRight, { className: "h-6 w-6 flex-shrink-0", style: { color: inactiveColor } }) })) })] }) }), _jsx(MobileSettingsBottomSheet, { isOpen: showSettings, onClose: () => setShowSettings(false) })] }));
}
export default MobileBottomNav;
